'use client';

import React, { useState, useCallback, useEffect } from 'react';
import clsx from 'clsx';
import { type UseEmblaCarouselType } from 'embla-carousel-react';
import { Picture } from 'app/components/Picture';
import { Video } from 'app/components/Video';
import {
    getS3ObjectUrl,
    getStaticImageSource,
    getDynamicImageSource,
} from 'app/libs/url';
import { type BuyerLayoutProps } from 'shop-ui/types';
import i18n from '@shared/i18n';
import { productImageGalleryTypeKey } from './define';
import { ImageGalleryNextButton } from './ImageGalleryNextButton';
import STYLE from './ProductImageGallery.module.scss';
import { type SlideOptions } from './ProductImageGallery';

type EmblaViewportRefType = UseEmblaCarouselType[0];

interface ImageGalleryViewportPropType extends BuyerLayoutProps<Product> {
    viewportRef: EmblaViewportRefType;
    api: {
        main: UseEmblaCarouselType[1];
    };
    slideOptions: SlideOptions['slideOptions'];
    isEditingMode: boolean;
}

type DefaultSlidePropType = {
    isFeatureType: boolean;
};

type SpecBadgePropType = {
    enabled: boolean;
    isBuyer: boolean;
    specName?: string;
};

const SpecBadge: React.FC<SpecBadgePropType> = (props) => {
    const { enabled, isBuyer, specName } = props;

    if (!enabled) {
        return null;
    }

    if (!isBuyer) {
        return <div className={STYLE.badge}>{i18n.gettext('規格')}</div>;
    }

    return specName ? <div className={STYLE.badge}>{specName}</div> : null;
};

const DefaultGalleryViewport: React.FC<DefaultSlidePropType> = (props) => {
    const { isFeatureType } = props;

    const source = useCallback((idx: number) => {
        return getStaticImageSource(
            getS3ObjectUrl(
                `/static/image-gallery/image-gallery-presentation-${idx + 1}.png`
            )
        );
    }, []);

    return Array(10)
        .fill(null)
        .map((_, idx) => (
            <div className={STYLE.slide} key={idx}>
                {isFeatureType && <div className={STYLE.badge}>規格</div>}
                <Picture
                    sources={[{ ...source(idx) }]}
                    alt={`Product Image - ${idx + 1}`}
                />
            </div>
        ));
};

export const ImageGalleryViewport: React.FC<ImageGalleryViewportPropType> = (props) => {
    const { api, viewportRef, slideOptions, isBuyer } = props;

    const isFeatureType = slideOptions.type === productImageGalleryTypeKey.feature;

    const [prevBtnEnabled, setPrevBtnEnabled] = useState(false);

    const [nextBtnEnabled, setNextBtnEnabled] = useState(false);

    useEffect(() => {
        if (!api.main) {
            return;
        }

        api.main.on('select', () => {
            setPrevBtnEnabled(api.main?.canScrollPrev() ?? false);
            setNextBtnEnabled(api.main?.canScrollNext() ?? false);
        });

        api.main.on('reInit', () => {
            setPrevBtnEnabled(api.main?.canScrollPrev() ?? false);
            setNextBtnEnabled(api.main?.canScrollNext() ?? false);
        });
    }, [api]);

    return (
        <div className={STYLE.galleryViewportBox}>
            <div className={STYLE.viewport} ref={viewportRef}>
                {prevBtnEnabled && (
                    <ImageGalleryNextButton
                        isFeatureType={isFeatureType}
                        slideOptions={slideOptions}
                        onClick={() => {
                            api.main?.scrollPrev();
                        }}
                        isPrevious
                    />
                )}
                <div
                    className={clsx(
                        STYLE.container,
                        slideOptions.type === productImageGalleryTypeKey.scroll &&
                            STYLE.scrollContainer
                    )}
                >
                    {!isBuyer && <DefaultGalleryViewport isFeatureType={isFeatureType} />}
                    {isBuyer &&
                        slideOptions.medias.map((media, idx) => {
                            const source = getDynamicImageSource(media.src);

                            return (
                                <div className={STYLE.slide} key={media.id + idx}>
                                    <SpecBadge
                                        enabled={isFeatureType}
                                        isBuyer={isBuyer}
                                        specName={media?.specName || ''}
                                    />
                                    {source.type.startsWith('image') && (
                                        <Picture
                                            defaultSrc={source.src}
                                            sources={[
                                                {
                                                    srcSet: source.srcSet,
                                                    type: source.type,
                                                },
                                            ]}
                                            alt={`Product Image - ${idx + 1}`}
                                        />
                                    )}
                                    {source.type.startsWith('video') && (
                                        <Video src={source.src} />
                                    )}
                                </div>
                            );
                        })}
                </div>
                {nextBtnEnabled && (
                    <ImageGalleryNextButton
                        isFeatureType={isFeatureType}
                        slideOptions={slideOptions}
                        onClick={() => {
                            api.main?.scrollNext();
                        }}
                    />
                )}
            </div>
        </div>
    );
};
